import { useState } from 'react';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useContentVerification, useClearVerificationBlock } from '../../hooks/useContent';
import type { ClaimVerdict, VerifiedClaim } from '../../api/content';
import { LoadingState } from '../../components/ui/States';

export interface VerificationPanelProps {
  contentItemId: string;
}

const VERDICT_LABEL: Record<ClaimVerdict, string> = {
  supported: 'Verified',
  contradicted: 'Contradicted',
  unsupported: 'No support found',
  unverifiable: 'Unverifiable',
};

/** Contradictions are probably wrong; unverifiable is merely unprovable. */
const VERDICT_TONE: Record<ClaimVerdict, 'success' | 'error' | 'warning'> = {
  supported: 'success',
  contradicted: 'error',
  unsupported: 'error',
  unverifiable: 'warning',
};

const VERDICT_ORDER: ClaimVerdict[] = ['contradicted', 'unsupported', 'unverifiable', 'supported'];

function ClaimRow({ claim }: { claim: VerifiedClaim }) {
  const [open, setOpen] = useState(false);
  const tone = VERDICT_TONE[claim.verdict];

  return (
    <div className="border-b border-border last:border-0 py-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left flex items-start gap-3"
      >
        <Badge variant={tone === 'success' ? 'success' : tone === 'error' ? 'error' : 'warning'}>
          {VERDICT_LABEL[claim.verdict]}
        </Badge>
        <span className="text-body-sm text-text-primary flex-1">{claim.verbatim}</span>
        <span className="text-body-xs text-text-tertiary shrink-0">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className="mt-2 pl-2 space-y-2 border-l-2 border-border">
          {claim.note && <div className="text-body-xs text-text-secondary">{claim.note}</div>}
          <div className="text-body-xs text-text-tertiary">
            {claim.kind.replace(/_/g, ' ')} · checked via {claim.method.replace(/-/g, ' ')}
          </div>
          {claim.evidence.map((e, i) => (
            <div key={i} className="text-body-xs text-text-tertiary">
              <span className="font-medium">{e.provider}</span>
              {e.url && (
                <>
                  {' — '}
                  <a href={e.url} target="_blank" rel="noreferrer" className="underline break-all">
                    {e.url}
                  </a>
                </>
              )}
              <div className="mt-0.5">{e.text}</div>
            </div>
          ))}
          {claim.evidence.length === 0 && (
            <div className="text-body-xs text-text-tertiary">No supporting source was found.</div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The fact-check, keyword targeting and link health for a generated article.
 * Renders nothing for items that never went through the impact pipeline.
 */
export function VerificationPanel({ contentItemId }: VerificationPanelProps) {
  const { data, isLoading } = useContentVerification(contentItemId);
  const clearBlock = useClearVerificationBlock();

  if (isLoading) {
    return <LoadingState />;
  }
  if (!data?.verification) return null;

  const v = data.verification;
  const total = Object.values(v.counts).reduce((a, b) => a + b, 0);
  const problems = v.claims.filter((c) => c.verdict !== 'supported');
  const ordered = [...v.claims].sort(
    (a, b) => VERDICT_ORDER.indexOf(a.verdict) - VERDICT_ORDER.indexOf(b.verdict),
  );

  return (
    <div className="space-y-4">
      {v.status === 'blocked' && (
        <Alert variant="error">
          <strong>{problems.length} claim{problems.length === 1 ? '' : 's'} could not be verified.</strong>{' '}
          Read them below before publishing. Nothing has been pushed to Sanity.
        </Alert>
      )}
      {v.status === 'cleared' && (
        <Alert variant="warning">
          Block cleared by {v.clearedBy ?? 'a reviewer'}
          {v.clearedAt ? ` on ${new Date(v.clearedAt).toLocaleDateString()}` : ''}. The unverified claims
          below are still in the article.
        </Alert>
      )}
      {v.status === 'passed' && (
        <Alert variant="success">All {total} claims traced to a source.</Alert>
      )}
      {v.error && <Alert variant="error">Verification did not complete: {v.error}</Alert>}

      <div className="flex flex-wrap gap-2 text-body-xs">
        <span className="text-text-secondary">{v.counts.supported} verified</span>
        {v.counts.contradicted > 0 && <span className="text-text-secondary">· {v.counts.contradicted} contradicted</span>}
        {v.counts.unsupported > 0 && <span className="text-text-secondary">· {v.counts.unsupported} unsupported</span>}
        {v.counts.unverifiable > 0 && <span className="text-text-secondary">· {v.counts.unverifiable} unverifiable</span>}
        {v.repairs.length > 0 && <span className="text-text-tertiary">· {v.repairs.length} auto-repaired</span>}
      </div>

      {data.keywordBrief && (
        <div className="rounded-2xl border border-border p-3 space-y-1">
          <div className="text-body-sm font-medium text-text-primary">Keyword target</div>
          <div className="text-body-sm text-text-secondary">
            <strong>{data.keywordBrief.headTerm}</strong>
            {data.keywordBrief.headVolume ? ` — ${data.keywordBrief.headVolume.toLocaleString()}/mo` : ''}
            {data.keywordBrief.headDifficulty !== null ? `, KD ${data.keywordBrief.headDifficulty}` : ''}
          </div>
          {data.keywordBrief.headTerm.toLowerCase() !== data.keywordBrief.topic.toLowerCase() && (
            <div className="text-body-xs text-text-tertiary">
              Requested "{data.keywordBrief.topic}" — the term above carries the search demand.
            </div>
          )}
          {data.keywordBrief.warnings.map((w) => (
            <div key={w} className="text-body-xs text-warning">{w}</div>
          ))}
        </div>
      )}

      {data.brokenLinks.length > 0 && (
        <Alert variant="warning">
          {data.brokenLinks.length} internal link{data.brokenLinks.length === 1 ? '' : 's'} did not resolve:{' '}
          {data.brokenLinks.join(', ')}
        </Alert>
      )}

      <div className="rounded-2xl border border-border p-3">
        <div className="text-body-sm font-medium text-text-primary mb-2">
          Claims ({total})
        </div>
        {ordered.map((claim) => (
          <ClaimRow key={claim.id} claim={claim} />
        ))}
      </div>

      {v.repairs.length > 0 && (
        <div className="rounded-2xl border border-border p-3 space-y-2">
          <div className="text-body-sm font-medium text-text-primary">Automatic repairs</div>
          {v.repairs.map((r) => (
            <div key={r.claimId} className="text-body-xs text-text-tertiary space-y-0.5">
              <div className="text-text-secondary">{r.action}: {r.reason}</div>
              <div className="line-through">{r.before}</div>
              {r.after && <div>{r.after}</div>}
            </div>
          ))}
        </div>
      )}

      {v.status === 'blocked' && (
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => clearBlock.mutate(contentItemId)}
            disabled={clearBlock.isPending}
          >
            {clearBlock.isPending ? 'Clearing…' : 'I have read these — clear the block'}
          </Button>
          <span className="text-body-xs text-text-tertiary">
            The claims stay on the record.
          </span>
        </div>
      )}
    </div>
  );
}
